import React, { useMemo } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { format } from "date-fns-tz";
import { BrazilDate } from "../../../../utils/BrazilDate";
import { NavigationButtons } from "../../Entrega/components/NavigationButtons";
import { LojaSelector } from "../../Entrega/components/LojaSelector";
import { Header } from "./Header";
import { FilaEmPreparoRetirada } from "./FilaEmPreparoRetirada";
import { FilaProntosRetirada } from "./FilaProntosRetirada";
import { PedidoProntoRetiradaModal } from "./PedidoProntoRetiradaModal";
import { useRetiradaPedidoProntoModal } from "../hooks/useRetiradaPedidoProntoModal";
import { useTvRetiradaRelogio } from "../hooks/useTvRetiradaRelogio";
import { FaixaPedidosDemorandoRetirada } from "./FaixaPedidosDemorandoRetirada";
import { partitionProntosRetiradaTv } from "../utils/splitProntosRetiradaTv";
import type { ClientePedidoStatusRow } from "../utils/mapPainelParaColunasCliente";
import type { Loja } from "../../../../types/loja";

export interface RankingStatusPedidosViewProps {
  emPreparo: ClientePedidoStatusRow[];
  prontos: ClientePedidoStatusRow[];
  lojas: Loja[];
  selectedLojaId?: number;
  showLojaSelector: boolean;
  showLojaSelectorUI: boolean;
  selectedLojaNome?: string;
  onGoHome: () => void;
  onToggleLojaSelector: () => void;
  onSelectAllLojas: () => void;
  onSelectLoja: (id: number) => void;
}

export const RankingStatusPedidosView: React.FC<
  RankingStatusPedidosViewProps
> = ({
  emPreparo,
  prontos,
  lojas,
  selectedLojaId,
  showLojaSelector,
  showLojaSelectorUI,
  selectedLojaNome,
  onGoHome,
  onToggleLojaSelector,
  onSelectAllLojas,
  onSelectLoja,
}) => {
  const tvAgora = useTvRetiradaRelogio();
  const { todosProntosHoje, prontosRecentesHoje, prontosDemorandoHoje } =
    useMemo(
      () => partitionProntosRetiradaTv(prontos, tvAgora),
      [prontos, tvAgora]
    );

  const {
    modalPedido,
    aguardandoDepoisEste,
    segundosRestantes,
    voiceEnabled,
    toggleVoice,
  } = useRetiradaPedidoProntoModal(emPreparo, todosProntosHoje);

  const timezone = BrazilDate.getTimezone();
  const dateLabelVivo = format(BrazilDate.now(), "dd/MM/yyyy", {
    timeZone: timezone,
  });

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-gradient-to-br from-primary via-red-900 to-primary text-white">
      {modalPedido && (
        <PedidoProntoRetiradaModal
          customerName={modalPedido.customerName}
          segundosRestantes={segundosRestantes}
          aguardandoDepoisEste={aguardandoDepoisEste}
        />
      )}

      <div className="fixed right-3 top-[4.75rem] z-[45] flex flex-col items-end gap-1 sm:right-5 sm:top-[5rem] md:top-24">
        <button
          type="button"
          onClick={() => toggleVoice()}
          aria-pressed={voiceEnabled}
          className={`flex items-center gap-2 rounded-full border-2 px-3 py-2 text-xs font-semibold backdrop-blur-sm transition-colors sm:text-sm ${
            voiceEnabled
              ? "border-green-400/70 bg-black/55 text-green-100 hover:bg-black/65"
              : "border-white/25 bg-black/35 text-white/90 hover:bg-black/45"
          }`}
          title={
            voiceEnabled
              ? "Desativar voz na TV de retirada"
              : "Ativar aviso por voz (navegador)"
          }
        >
          {voiceEnabled ? (
            <Volume2 size={18} aria-hidden />
          ) : (
            <VolumeX size={18} aria-hidden />
          )}
          <span className="max-w-[7.5rem] leading-tight sm:max-w-none">
            {voiceEnabled ? "Voz ligada" : "Voz off"}
          </span>
        </button>
      </div>

      <NavigationButtons
        lojasCount={lojas.length}
        onGoHome={onGoHome}
        onToggleLojaSelector={onToggleLojaSelector}
        showLojaButton={showLojaSelectorUI}
      />

      {showLojaSelectorUI && (
        <LojaSelector
          lojas={lojas}
          selectedLojaId={selectedLojaId}
          showLojaSelector={showLojaSelector}
          onSelectAllLojas={onSelectAllLojas}
          onSelectLoja={onSelectLoja}
        />
      )}

      <Header
        dateLabel={dateLabelVivo}
        selectedLojaId={selectedLojaId}
        selectedLojaNome={selectedLojaNome}
      />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4 pt-2 sm:px-6 lg:px-8">
        <div className="mx-auto flex min-h-0 w-full max-w-[min(100%,108rem)] flex-1 flex-col">
          <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2 lg:gap-8">
            <FilaEmPreparoRetirada itens={emPreparo} />
            <FilaProntosRetirada itens={prontosRecentesHoje} />
          </div>
        </div>

        <FaixaPedidosDemorandoRetirada
          itens={prontosDemorandoHoje}
          agora={tvAgora}
          dateLabel={dateLabelVivo}
        />
      </div>
    </div>
  );
};
